import { FC, useEffect, useState } from "react";
import "./transactions.css";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { DigitalWalletOwnerInfoResponse, TransactionFilterRequest, TransactionRowResult } from "@/models/transaction";
import digitalWalletStore from "@/stores/digital.wallet.store";
import popupService from "@/services/popupService";
import { CacheProvider } from "@emotion/react";
import authStore from "@/stores/authStore";
import { Grid } from "@mui/material";
import TransactionsFilter from "@/components/transactions/transactions.filter";
import AllTransactionsGrid from "@/components/transactions/all.transactions.grid";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TotalCountComponent from "@/components/common/resultCountComponent";
import TransactionsInfoTab from "@/components/transactions/transactions.info.tab";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const AllTransactionsPage: FC = () => {
    const { t } = useTranslation();
    const [hasNext, setHasNext] = useState<boolean>(true);
    const [transactions, setTransactions] = useState<TransactionRowResult[]>([]);

    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);
    const [filterOrderBy, setFilterByOrderBy] = useState<string>("Id");
    const [filterAsc, setFilterAsc] = useState<boolean>(true);
    const [searchCounter, setSearchCounter] = useState<number>(0);
    const [isSearch, setIsSearch] = useState<boolean>(false);
    const [selectedTransaction, setSelectedTransaction] = useState<TransactionRowResult | null>(null);

    const [filter, setFilter] = useState(new TransactionFilterRequest(null));

    async function startFetching() {
        setIsSearch(() => true);
        try {
            var result = await digitalWalletStore.getAllTransactions(filter);
            if (result) {
                setHasNext(result.hasNext);
                if (filter.pageIndex === 0) {
                    setTransactions(result.rows);
                    setTotal(result.total);
                    setTotalFilter(result.totalFilter);
                } else {
                    setTransactions((old) => [...old, ...result.rows]);
                }
                setIsSearch(() => false);
            }
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            setIsSearch(() => false);
        }
    }

    const searchTransactionsClick = () => {
        setTransactions([]);
        setPageIndex(0);
        setSearchCounter(searchCounter + 1);
    };

    const setPageIndex = (index?: number) => {
        setFilter((prevData) => ({
            ...prevData,
            pageIndex: index ?? filter.pageIndex + 1,
        }));
    };

    async function sortBy(sortBy: string) {
        if (filterOrderBy !== sortBy) {
            setFilterAsc(true);
        } else {
            setFilterAsc(!filterAsc);
        }
        setPageIndex(0);
        setTransactions([]);
        setFilterByOrderBy(sortBy);
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [filter.pageIndex, filterOrderBy, filterAsc, searchCounter]);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <Grid className="transactions-main-block">
                {/* {selectedTransaction ? (
                    <TransactionsInfoTab
                        selectedTransaction={selectedTransaction}
                        setSelectedTransaction={setSelectedTransaction}
                    />
                ) : (
                    ""
                )} */}

                <TransactionsFilter
                    searchClick={searchTransactionsClick}
                    filter={filter}
                    setFilter={setFilter}
                ></TransactionsFilter>
                <TotalCountComponent
                    allCount={total}
                    currentCount={totalFilter}
                    title={t("digitalWallet.QuantityOfTransactions") as string}
                />
                <AllTransactionsGrid
                    sortBy={sortBy}
                    transactions={transactions}
                    isSearch={isSearch}
                    fetchMore={setPageIndex}
                    hasNext={hasNext}
                    setSelectedTransaction={setSelectedTransaction}
                ></AllTransactionsGrid>
            </Grid>
        </CacheProvider>
    );
};

export default AllTransactionsPage;
